import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBars, FaCog, FaGlobe, FaSignInAlt, FaSignOutAlt, FaTimes, FaUserCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import LoginModal from './LoginModal';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const logoUrl = '/pictures/LOGO.png';
  const navigationItems = [
    { to: '/', text: t('nav.home') },
    { to: '/freebord', text: t('nav.freebord') },
    { to: '/products', text: t('nav.ourProducts') },
    { to: '/helmets', text: t('nav.helmets') },
    { to: '/diy', text: t('nav.diy') },
    { to: '/safety', text: t('nav.safety') },
    { to: '/protective-equipment', text: t('nav.protectiveEquipment') },
    { to: '/about', text: t('nav.about') },
    { to: '/service', text: t('nav.service') }
  ];

  const toggleLanguage = () => {
    const newLang = i18n.language === 'he' ? 'en' : 'he';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'he' ? 'rtl' : 'ltr';
    setSettingsOpen(false);
  };

  const closeMobileMenu = () => setIsMobileOpen(false);

  return (
    <>
      <nav className="fixed z-50 w-full bg-white shadow-md">
        <div className="container mx-auto flex items-center justify-between px-6 py-3">
          <NavLink to="/" className="flex items-center" onClick={closeMobileMenu}>
            <img src={logoUrl} alt="BlueBoard Logo" className="h-10 w-auto" />
          </NavLink>

          <div className="hidden items-center gap-1 md:flex">
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 ${
                    isActive ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`
                }
              >
                {item.text}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={() => setSettingsOpen((open) => !open)}
                aria-label="Settings"
              >
                <FaCog className="h-6 w-6 text-gray-700" />
              </button>
              {settingsOpen && (
                <div className={`absolute top-14 z-50 min-w-[220px] rounded-lg border bg-white p-4 shadow-lg ${i18n.language === 'he' ? 'left-0' : 'right-0'}`}>
                  <button
                    className="mb-2 flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-gray-100"
                    onClick={toggleLanguage}
                  >
                    <FaGlobe />
                    {i18n.language === 'he' ? 'English' : 'עברית'}
                  </button>
                  <hr className="my-2" />
                  {user ? (
                    <>
                      <div className="mb-3 flex items-center gap-2">
                        <FaUserCircle className="h-6 w-6 text-blue-600" />
                        <div>
                          <div className="font-bold">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                      <button
                        className="flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-gray-100"
                        onClick={() => {
                          navigate('/profile');
                          setSettingsOpen(false);
                        }}
                      >
                        <FaUserCircle />
                        {t('nav.profile')}
                      </button>
                      <button
                        className="flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-gray-100"
                        onClick={() => {
                          logout();
                          setSettingsOpen(false);
                        }}
                      >
                        <FaSignOutAlt />
                        {t('nav.logout')}
                      </button>
                    </>
                  ) : (
                    <button
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-gray-100"
                      onClick={() => {
                        setShowLogin(true);
                        setSettingsOpen(false);
                      }}
                    >
                      <FaSignInAlt />
                      {t('nav.login')}
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              className="rounded-full p-2 hover:bg-gray-100 md:hidden"
              onClick={() => setIsMobileOpen((open) => !open)}
              aria-label="Menu"
            >
              {isMobileOpen ? <FaTimes className="h-6 w-6 text-gray-700" /> : <FaBars className="h-6 w-6 text-gray-700" />}
            </button>
          </div>
        </div>

        {isMobileOpen && (
          <div className="border-t bg-white px-6 py-4 md:hidden">
            <div className="flex flex-col gap-2">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeMobileMenu}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2 ${
                      isActive ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-200'
                    }`
                  }
                >
                  {item.text}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </>
  );
};

export default Navbar;
